import { ContentCopy } from '@mui/icons-material';
import { Box, Button, IconButton, styled, TextField, Tooltip } from '@mui/material';
import moment from 'moment';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CheckboxGroup, DatePicker, useLoader } from 'thermia-react-container';
import useApiClient from '../../api/api-client';
import { ApiKey, ApiKeySettings, Device } from '../../api/api-types';
import { DEFAULT_API_KEY_EXPIRES } from '../../business-rules/business-rules';
import ErrorComponent from '../../components/error-component';
import InlineLoader from '../../components/inline-loader';

const CreateKeyComponent: FC<{ device: Device; onKeyCreated: () => void }> = ({ device, onKeyCreated }) => {
  const { Loader } = useLoader();
  const { getKeySettings, createKey } = useApiClient();
  const { t } = useTranslation('portal');

  const [initStatus, setInitStatus] = useState<'initializing' | 'error' | 'initialized'>('initializing');
  const [settings, setSettings] = useState<ApiKeySettings | null>(null);
  const [description, setDescription] = useState('');
  const [expires, setExpires] = useState(DEFAULT_API_KEY_EXPIRES);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [createdKey, setCreatedKey] = useState<ApiKey | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      try {
        const keySettings = await getKeySettings();
        setSettings(keySettings);
        setSelectedPermissions(keySettings.permissions);
        setInitStatus('initialized');
      } catch {
        setInitStatus('error');
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async () => {
    if (!isSubmitting) {
      try {
        setIsSubmitting(true);
        setSubmitError(null);
        const key = await createKey({
          installationId: device.installationId,
          description,
          expires,
          permissions: selectedPermissions
        });
        setCreatedKey(key);
        onKeyCreated();
      } catch {
        setSubmitError(t('portal.keys.create-key-component.error.create'));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleCopy = () => {
    if (createdKey) {
      navigator.clipboard.writeText(createdKey.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (initStatus === 'initializing') return <Loader />;
  if (initStatus === 'error') return <ErrorComponent text={t('portal.keys.create-key-component.error.init')} />;
  if (!settings) return null;

  if (createdKey) {
    return (
      <>
        <p>{t('portal.keys.create-key-component.keyCreatedTitle')}</p>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              color: 'black !important',
              backgroundColor: '#fff4e5',
              border: '1px solid #f5a623',
              borderRadius: 1,
              padding: '20px 32px',
              fontFamily: 'monospace',
              fontSize: '0.9rem',
              wordBreak: 'break-all'
            }}>
            {createdKey.key}
          </Box>
          <Tooltip title={copied ? t('portal.keys.create-key-component.copied') : t('portal.keys.create-key-component.copy')}>
            <IconButton onClick={handleCopy} size="small">
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ marginTop: 2 }}>
          <Button variant="outlined" component={Link} to="/keys">
            {t('portal.keys.create-key-component.backToKeys')}
          </Button>
        </Box>
      </>
    );
  }

  return (
    <>
      <p>{t('portal.keys.create-key-component.title')}</p>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
        <TextField label={t('portal.keys.create-key-component.description')} value={description} onChange={(e) => setDescription(e.target.value)} />
        <DatePicker
          label={t('portal.keys.create-key-component.expires')}
          value={expires}
          onChange={setExpires}
          minDate={moment()}
          maxDate={moment().add(settings.maxExpirationInYears, 'years')}
        />
        <HorizontalCheckboxStyling>
          <CheckboxGroup
            label={t('portal.keys.create-key-component.permissions')}
            options={settings.permissions.map((permission) => ({ label: permission, value: permission }))}
            values={selectedPermissions}
            onValueChange={setSelectedPermissions}
          />
        </HorizontalCheckboxStyling>
        <Button
          variant="contained"
          color="primary"
          onClick={onSubmit}
          disabled={!description.trim() || !expires || selectedPermissions.length === 0 || isSubmitting}>
          {t('portal.keys.create-key-component.submit')}
        </Button>
        {isSubmitting && <InlineLoader loader={Loader} />}
        {submitError && <ErrorComponent text={submitError} />}
      </Box>
    </>
  );
};

const HorizontalCheckboxStyling = styled('div')(() => ({
  '& .MuiFormGroup-root:has(> label)': {
    flexDirection: 'row'
  }
}));

export default CreateKeyComponent;
